import { isOrderableQuantity } from '../../constants/orderQuantity';
import { PoReviewDraft, PoReviewRow } from '../../redux/types';
import { hasLiveUnforcedConcern } from './poReviewFindings';

export enum PoReviewRowStatus {
    OK = 'OK',
    WARNING = 'WARNING',
    ERROR = 'ERROR',
}

export function isNeutralAddedRow(row: PoReviewRow, submitAttempted: boolean): boolean {
    return row.isAdded && !submitAttempted;
}

// A row's status is its worst live concern: any empty required field is Error, any live unforced
// concern is Review, none is OK. Forced concerns clear as their field is filled, so a filled row falls
// out of Error and Review on its own.
export const MATCHES_STATUS: Record<PoReviewRowStatus, (row: PoReviewRow) => boolean> = {
    [PoReviewRowStatus.ERROR]: (row) => !row.itemId || !isOrderableQuantity(row.quantity),
    [PoReviewRowStatus.WARNING]: (row) => hasLiveUnforcedConcern(row),
    [PoReviewRowStatus.OK]: (row) => rowStatus(row) === PoReviewRowStatus.OK,
};

export function rowStatus(row: PoReviewRow): PoReviewRowStatus {
    if (MATCHES_STATUS[PoReviewRowStatus.ERROR](row)) {
        return PoReviewRowStatus.ERROR;
    }
    if (MATCHES_STATUS[PoReviewRowStatus.WARNING](row)) {
        return PoReviewRowStatus.WARNING;
    }
    return PoReviewRowStatus.OK;
}

export function isStillAlreadyOrdered(draft: PoReviewDraft | undefined): boolean {
    return !!draft?.alreadyOrderedPoNumber && draft.poNumber === draft.alreadyOrderedPoNumber;
}

export function isPoNumberInError(draft: PoReviewDraft): boolean {
    return !draft.poNumber || isStillAlreadyOrdered(draft);
}

export type SubmitBlocker =
    | { kind: 'poNumber' }
    | { kind: 'duplicatePoNumber' }
    | { kind: 'shipDate' }
    | { kind: 'shippingLocation' }
    | { kind: 'lineItems' }
    | { kind: 'item' | 'quantity'; rowId: string };

// Empty when the draft can be submitted; otherwise the required fields still missing, in page order.
export function submitBlockers(draft: PoReviewDraft | undefined, selectableLocationCodes: string[]): SubmitBlocker[] {
    const blockers: SubmitBlocker[] = [];
    if (!draft?.poNumber) {
        blockers.push({ kind: 'poNumber' });
    }

    if (isStillAlreadyOrdered(draft)) {
        blockers.push({ kind: 'duplicatePoNumber' });
    }
    if (!draft?.shipDate) {
        blockers.push({ kind: 'shipDate' });
    }
    if (!draft?.shippingLocationCode || !selectableLocationCodes.includes(draft.shippingLocationCode)) {
        blockers.push({ kind: 'shippingLocation' });
    }
    const rows = draft?.rows ?? [];
    if (rows.length === 0) {
        blockers.push({ kind: 'lineItems' });
    }
    rows.forEach((row) => {
        if (!row.itemId) {
            blockers.push({ kind: 'item', rowId: row.rowId });
        }
        if (!isOrderableQuantity(row.quantity)) {
            blockers.push({ kind: 'quantity', rowId: row.rowId });
        }
    });
    return blockers;
}
